import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, Users, Battery, Zap, Sparkles, AlertTriangle,
  Phone, Mail, ChevronRight, Building2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { opportunitiesAPI } from '../lib/api';
import { formatPhoneNumber } from '../lib/utils';
import { toast } from 'sonner';

const OpportunityCard = ({ opportunity, onClick }) => {
  const hasBatteryOpp = opportunity.battery_upsell;
  const hasEVOpp = opportunity.ev_charger_upsell;
  const hasOldInstallations = opportunity.old_installations?.length > 0;
  const needsCleaning = opportunity.needs_cleaning?.length > 0;

  return (
    <Card 
      className="card-hover cursor-pointer" 
      onClick={onClick}
      data-testid={`opportunity-card-${opportunity.customer_id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <span className="text-sm font-medium text-primary">
                {opportunity.customer_name.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            <div>
              <p className="font-medium">{opportunity.customer_name}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Mail className="w-3 h-3" />
                <span>{opportunity.email || 'Geen email'}</span>
              </div>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </div>

        {/* Kansen Tags */}
        <div className="flex flex-wrap gap-2">
          {hasBatteryOpp && (
            <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
              <Battery className="w-3 h-3 mr-1" />
              Batterij kans
            </Badge>
          )}
          {hasEVOpp && (
            <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
              <Zap className="w-3 h-3 mr-1" />
              Laadpaal kans
            </Badge>
          )}
          {needsCleaning && (
            <Badge variant="outline" className="bg-purple-500/20 text-purple-400 border-purple-500/30">
              <Sparkles className="w-3 h-3 mr-1" />
              Reiniging nodig ({opportunity.needs_cleaning.length})
            </Badge>
          )}
          {hasOldInstallations && (
            <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Oude installatie ({opportunity.old_installations.length})
            </Badge>
          )}
        </div>

        {/* Aantal adressen */}
        {opportunity.property_count > 0 && (
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            <Building2 className="w-3 h-3" />
            <span>{opportunity.property_count} {opportunity.property_count === 1 ? 'adres' : 'adressen'}</span>
          </div>
        )}

        {/* Contact */}
        {opportunity.phone && (
          <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
            <a href={`tel:${opportunity.phone}`} className="flex items-center gap-1 hover:text-primary">
              <Phone className="w-4 h-4" />
              {formatPhoneNumber(opportunity.phone)}
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Opportunities = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await opportunitiesAPI.getAll();
        setOpportunities(response.data);
      } catch (error) {
        toast.error('Failed to load opportunities');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredOpportunities = opportunities.filter(opp => {
    switch (activeTab) {
      case 'battery':
        return opp.battery_upsell;
      case 'ev_charger':
        return opp.ev_charger_upsell;
      case 'cleaning':
        return opp.needs_cleaning?.length > 0;
      case 'old':
        return opp.old_installations?.length > 0;
      default:
        return true;
    }
  });

  const counts = {
    all: opportunities.length,
    battery: opportunities.filter(o => o.battery_upsell).length,
    ev_charger: opportunities.filter(o => o.ev_charger_upsell).length,
    cleaning: opportunities.filter(o => o.needs_cleaning?.length > 0).length,
    old: opportunities.filter(o => o.old_installations?.length > 0).length,
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="opportunities-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Verkoopkansen</h1>
        <p className="text-muted-foreground mt-1">Slimme detectie van upsell- en onderhoudskansen</p>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Battery className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-2xl font-bold">{counts.battery}</p>
                <p className="text-xs text-muted-foreground">Batterij kansen</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Zap className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-2xl font-bold">{counts.ev_charger}</p>
                <p className="text-xs text-muted-foreground">Laadpaal kansen</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-purple-400" />
              <div>
                <p className="text-2xl font-bold">{counts.cleaning}</p>
                <p className="text-xs text-muted-foreground">Reiniging nodig</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-amber-400" />
              <div>
                <p className="text-2xl font-bold">{counts.old}</p>
                <p className="text-xs text-muted-foreground">Oude installaties</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">Alles ({counts.all})</TabsTrigger>
          <TabsTrigger value="battery">Batterij ({counts.battery})</TabsTrigger>
          <TabsTrigger value="ev_charger">Laadpaal ({counts.ev_charger})</TabsTrigger>
          <TabsTrigger value="cleaning">Reiniging ({counts.cleaning})</TabsTrigger>
          <TabsTrigger value="old">Oude inst. ({counts.old})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="h-40 animate-pulse bg-muted" />
              ))}
            </div>
          ) : filteredOpportunities.length === 0 ? (
            <Card className="p-12 text-center">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Geen kansen gevonden in deze categorie</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredOpportunities.map((opp) => (
                <OpportunityCard 
                  key={opp.customer_id} 
                  opportunity={opp}
                  onClick={() => navigate(`/customers/${opp.customer_id}`)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Opportunities;
